import { useEffect, useState } from 'react';
import { ArrowRightLeft, X, PlayCircle, ShieldCheck, Loader2, AlertOctagon, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { provisioningAPI, customersAPI } from '../services/api.js';
import { ReadinessBadge, ReadinessChecks } from './LineReadiness.jsx';
import { formatPhone } from '../lib/format.js';

// REASSIGN A NUMBER to a different tenant.
//
// Four layers have to move together, and the whole reason this screen exists is
// that they can disagree:
//
//   1. the `phone_numbers` row        (the database)
//   2. the Telnyx connection          (who receives the call)
//   3. the LiveKit inbound trunk      (which agent deployment answers)
//   4. panel env gates on the box     (which can bypass ALL of the above)
//
// 832 is the case study: its DB row said Alfred Voice for over 24 hours while
// `GROK_VOICE_TEST_DID` kept routing it to the test stack, because that gate
// returns BEFORE `resolve_tenant` runs. So a reassignment that only moved layer 1
// looked complete and rang the old tenant.
//
// ⛔ THEREFORE: an env-gate shadow is a BLOCKER, not a warning. It does not merely
// annotate the plan — it DISABLES the apply button. A green checkmark on a move the
// box is ignoring is worse than an error, because it ends the investigation.
//
// Dry run first, always: `confirm` defaults false server-side and this never sends
// true until the operator has read the plan and pressed a second, differently
// labelled button. After applying, the per-layer READ-BACK is shown — verification
// from the live systems, not an echo of what we asked for.

const LAYER_LABEL = {
  phone_number: 'Database record',
  telnyx_connection: 'Telnyx → the box',
  livekit_trunk: 'LiveKit inbound trunk',
  env_shadow: 'Panel env gates on the box',
  panel_env: 'Panel env gates on the box'
};

// A step whose severity marks it as a shadow/override is disqualifying, regardless
// of whether core called it "ok" — being *routable* is not the same as being routed
// to the tenant you just assigned it to.
const isBlockingShadow = (s) =>
  (s?.step === 'env_shadow' || s?.step === 'panel_env') && (s?.shadowed === true || s?.ok === false);

function LayerRow({ s, applied }) {
  const blocking = isBlockingShadow(s);
  const Icon = blocking ? AlertOctagon : s.ok ? CheckCircle2 : AlertTriangle;
  const tone = blocking ? 'text-rose-300' : s.ok ? 'text-emerald-300' : 'text-amber-300';
  return (
    <li className="flex gap-2 py-2 text-sm">
      <Icon size={15} className={`mt-0.5 shrink-0 ${tone}`} />
      <div className="min-w-0">
        <div className="text-stone-200">
          {LAYER_LABEL[s.step] || s.step}
          {s.changed && (
            <span className="ml-2 badge bg-amber-950/60 text-amber-300 text-[10px]">
              {applied ? 'changed' : 'would change'}
            </span>
          )}
          {!s.changed && s.ok && (
            <span className="ml-2 text-xs text-stone-500">already correct</span>
          )}
        </div>
        {s.detail && <div className="text-xs text-stone-500">{s.detail}</div>}
        {s.fix && <div className="mt-0.5 text-xs text-stone-300">→ {s.fix}</div>}
        {s.error && <div className="text-xs text-rose-300">{s.error}</div>}
      </div>
    </li>
  );
}

export default function ReassignNumber({ did, currentTenantName, onDone }) {
  const [tenants, setTenants] = useState([]);
  const [toTenantId, setToTenantId] = useState('');
  const [plan, setPlan] = useState(null);
  const [applied, setApplied] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    customersAPI
      .list()
      .then((r) => setTenants((r?.data || []).filter((t) => t.status === 'active')))
      .catch(() => setTenants([]));
  }, []);

  const reset = () => { setPlan(null); setApplied(null); setError(''); };

  const run = async (confirm) => {
    setBusy(true);
    setError('');
    try {
      const r = await provisioningAPI.reassign({ did, toTenantId, confirm });
      const result = r?.data || r;
      if (confirm) setApplied(result); else setPlan(result);
    } catch (err) {
      // Not deployed yet — say so plainly instead of showing a broken control.
      if (err.status === 404) setUnavailable(true);
      else setError(err.body?.message || err.message || 'Reassignment call failed.');
    } finally {
      setBusy(false);
    }
  };

  if (unavailable) {
    return (
      <div className="rounded-lg border border-stone-700 bg-stone-900/40 p-4 text-sm text-stone-400">
        Reassigning a number isn’t available on this server yet.
        <button type="button" className="btn-ghost ml-3" onClick={() => onDone?.(false)}>
          <X size={13} /> Close
        </button>
      </div>
    );
  }

  const result = applied || plan;
  const steps = result?.steps || [];
  const shadows = steps.filter(isBlockingShadow);
  const blocked = shadows.length > 0;
  const target = tenants.find((t) => t.id === toTenantId);

  return (
    <div className="rounded-lg border border-alfred-gold/30 bg-stone-950/40 p-5">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-lg text-stone-100">
            <ArrowRightLeft size={16} className="text-alfred-gold" /> Move {formatPhone(did)} to another account
          </h3>
          <p className="mt-1 text-sm text-stone-400">
            Currently {currentTenantName || 'unassigned'}. Four things have to move together — the
            record, Telnyx, the LiveKit trunk, and the box’s own overrides.
          </p>
        </div>
        <button type="button" className="btn-ghost" onClick={() => onDone?.(!!applied)}>
          <X size={14} /> Close
        </button>
      </div>

      {error && (
        <p className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-200">{error}</p>
      )}

      <label className="block max-w-md">
        <span className="field-label">Move it to</span>
        <select
          className="field-input"
          value={toTenantId}
          onChange={(e) => { setToTenantId(e.target.value); reset(); }}
          disabled={busy || !!applied}
        >
          <option value="">Choose an account…</option>
          {tenants.map((t) => (
            <option key={t.id} value={t.id}>{t.companyName}</option>
          ))}
        </select>
      </label>

      <div className="mt-4 flex flex-wrap gap-2">
        {!applied && (
          <button type="button" className="btn-ghost" onClick={() => run(false)} disabled={busy || !toTenantId}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : <PlayCircle size={14} />}
            Check what this would do
          </button>
        )}
        {plan && !applied && (
          <button
            type="button"
            className="btn-primary"
            onClick={() => run(true)}
            /* The blocker, enforced — not annotated. */
            disabled={busy || blocked}
            title={blocked ? 'Blocked: the box would ignore this move.' : ''}
          >
            <ShieldCheck size={14} /> Move it
          </button>
        )}
      </div>

      {plan && !applied && !blocked && (
        <p className="mt-2 text-xs text-stone-400">
          Nothing has changed yet — this is the plan. Telnyx and LiveKit are only touched when you
          press Move it.
        </p>
      )}

      {/* ⛔ The 832 lesson, made unmissable and made binding. */}
      {blocked && (
        <div className="mt-4 rounded-lg border border-rose-500/50 bg-rose-950/40 p-4" role="alert">
          <div className="flex items-start gap-3">
            <AlertOctagon size={18} className="mt-0.5 shrink-0 text-rose-300" />
            <div className="min-w-0">
              <h4 className="font-semibold text-rose-200">
                Blocked — the box would ignore this move
              </h4>
              <p className="mt-1 text-sm text-stone-300">
                An override on the box claims this number, and it is read <strong>before</strong> the
                account is looked up. Moving the record now would show this line as
                {target ? ` ${target.companyName}` : ' the new account'} while callers still reach the
                old one. That is exactly how 832 sat wrong for a day.
              </p>
              <ul className="mt-2 space-y-1">
                {shadows.map((s) => (
                  <li key={s.step} className="text-xs">
                    <span className="font-mono text-rose-200">{s.envVar || s.step}</span>
                    {s.detail ? <span className="text-stone-400"> — {s.detail}</span> : null}
                    {s.fix ? <div className="text-stone-300">→ {s.fix}</div> : null}
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-stone-400">
                Clear the override on the box first, then re-check. This stays disabled until it’s gone.
              </p>
            </div>
          </div>
        </div>
      )}

      {result && (
        <div className="mt-4 rounded-lg border border-stone-800 bg-stone-950/60 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-stone-200">{applied ? 'What changed' : 'The plan'}</span>
            {result.readiness && <ReadinessBadge status={result.readiness.status} />}
          </div>
          <ul className="mt-2 divide-y divide-stone-900">
            {steps.map((s) => <LayerRow key={s.step} s={s} applied={!!applied} />)}
          </ul>

          {/* Read-back AFTER the move: recomputed from the live systems, not an echo
              of the request. "It says it worked" and "it works" are different claims. */}
          {applied && result.readiness && (
            <div className="mt-4 border-t border-stone-800 pt-3">
              <div className="mb-2 text-xs uppercase tracking-wide text-stone-500">
                Checked against the live systems afterwards
              </div>
              <ReadinessChecks checks={result.readiness.checks} did={result.did || did} />
            </div>
          )}

          {applied && result.state && result.state !== 'active' && (
            <p className="mt-3 text-sm text-amber-300">
              Partly moved — it stopped at {LAYER_LABEL[result.state] || result.state}. It is safe to
              run again; it picks up where it stopped.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
